import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Logo from 'components/common/Logo';
import Section from 'components/common/Section';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { showAlert } from 'stores/modal';
import api from 'util/api';
import logger from 'util/logger';

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isNotFoundError = err => {
  const status = getErrorStatus(err);

  return status === 404 || err?.Code === 'NotFound' || err?.code === 'NotFound';
};

const getErrorStatus = err => {
  return (
    err?.StatusCode ||
    err?.status ||
    err?.statusCode ||
    err?.response?.StatusCode ||
    err?.response?.status ||
    err?.response?.statusCode
  );
};

const getUnableToVerifyMessage = err => {
  const status = getErrorStatus(err);
  if (!status) {
    return 'There was a problem loading the document. Please try again.';
  }

  return `There was a problem loading the document. The server returned HTTP ${status}.`;
};

const emptyValue = 'Not provided';

const formatDateTime = value => {
  if (!value) {
    return emptyValue;
  }

  return moment.utc(value).local().format('LLL');
};

const formatDate = value => {
  if (!value) {
    return emptyValue;
  }

  return moment(value).format('l');
};

const formatCommissionStatus = status => {
  switch (status) {
    case 'ActiveAtTime':
      return 'Active at time';
    case 'InactiveAtTime':
      return 'Inactive at time';
    case 'Unverifiable':
      return 'Unable to verify';
    default:
      return emptyValue;
  }
};

const getCommissionStatusClassName = status => {
  switch (status) {
    case 'ActiveAtTime':
      return 'badge bg-success';
    case 'InactiveAtTime':
      return 'badge bg-danger';
    case 'Unverifiable':
      return 'badge bg-warning text-dark';
    default:
      return 'badge bg-secondary';
  }
};

const DetailRow = ({ label, children }) => (
  <div className="mb-3">
    <div className="text-700 fw-semi-bold fs--1">{label}</div>
    <div className="text-break">{children || emptyValue}</div>
  </div>
);

const NameList = ({ items }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <span>{emptyValue}</span>;
  }

  return (
    <ul className="mb-0 ps-3">
      {items.map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ul>
  );
};

const VerificationSection = ({ title, children }) => (
  <section className="border-top pt-4 mt-4">
    <h5 className="mb-3">{title}</h5>
    {children}
  </section>
);

const isVerificationResponse = response => {
  return (
    response &&
    response.VerificationDetails &&
    response.NotaryDetails &&
    response.NotarizationDetails
  );
};

const VerifyDocument = () => {
  const dispatch = useDispatch();
  const [documentGuid, setDocumentGuid] = useState('');
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const normalizedGuid = documentGuid.trim();
  const verificationDetails = verification?.VerificationDetails;
  const notaryDetails = verification?.NotaryDetails;
  const notarizationDetails = verification?.NotarizationDetails;

  const showValidationAlert = () => {
    dispatch(
      showAlert(
        'Invalid Document GUID',
        'Enter a valid document GUID and try again.'
      )
    );
  };

  const handleDocumentGuidChange = evt => {
    setDocumentGuid(evt.target.value);
    setVerification(null);
  };

  const handleSubmit = evt => {
    evt.preventDefault();

    if (!GUID_PATTERN.test(normalizedGuid)) {
      showValidationAlert();
      return;
    }

    setLoading(true);
    setVerification(null);
    api.DocumentPublic.getVerification(normalizedGuid)
      .then(response => {
        if (!isVerificationResponse(response)) {
          dispatch(
            showAlert(
              'Unable to Verify Document',
              'The server did not return a document verification summary. Please try again.'
            )
          );
          return;
        }

        setVerification(response);
      })
      .catch(err => {
        logger.log('error loading public document', err);
        if (isNotFoundError(err)) {
          dispatch(
            showAlert(
              <strong>Document Not Found</strong>,
              "We're sorry, but we could not find a document with the identifier you specified."
            )
          );
        } else {
          dispatch(
            showAlert(
              'Unable to Verify Document',
              getUnableToVerifyMessage(err)
            )
          );
        }
      })
      .finally(() => {
        setLoading(false);
        window.setTimeout(() => inputRef.current?.focus(), 0);
      });
  };

  return (
    <Section className="py-0">
      <Row className="flex-center min-vh-100 py-6">
        <Col
          sm={10}
          md={8}
          lg={verification ? 8 : 6}
          xl={verification ? 7 : 5}
          className={verification ? '' : 'col-xxl-4'}
        >
          <Logo width={40} />
          <Card>
            <Card.Body className="p-4 p-sm-5">
              <Form onSubmit={handleSubmit}>
                <p className="mb-3">
                  Please enter the unique identifier of the document you wish to
                  verify and click "Verify Document".
                </p>
                <Form.Group className="mb-3" controlId="publicDocumentGuid">
                  <Form.Label>Document GUID</Form.Label>
                  <Form.Control
                    ref={inputRef}
                    type="text"
                    value={documentGuid}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    disabled={loading}
                    onChange={handleDocumentGuidChange}
                    autoComplete="off"
                  />
                </Form.Group>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || normalizedGuid.length === 0}
                  className="w-100"
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <FontAwesomeIcon icon={faSearch} size="sm" />
                  )}{' '}
                  Verify
                </Button>
              </Form>
              {verification && (
                <div data-testid="document-verification-summary">
                  <VerificationSection title="Verification Details">
                    <DetailRow label="Unique Identifier">
                      {verificationDetails?.Identifier}
                    </DetailRow>
                    <DetailRow label="Signing Timestamp">
                      {formatDateTime(verificationDetails?.SigningTimestamp)}
                    </DetailRow>
                    <DetailRow label="Verification">
                      {verificationDetails?.VerificationMessage}
                    </DetailRow>
                    <DetailRow label="Commission Status">
                      <span
                        className={getCommissionStatusClassName(
                          verificationDetails?.CommissionStatus
                        )}
                      >
                        {formatCommissionStatus(
                          verificationDetails?.CommissionStatus
                        )}
                      </span>
                    </DetailRow>
                    <DetailRow label="Declared Commission Expiration">
                      {formatDate(
                        verificationDetails?.DeclaredCommissionExpirationDate
                      )}
                    </DetailRow>
                    <DetailRow label="Commission Verification">
                      {verificationDetails?.CommissionMessage}
                    </DetailRow>
                  </VerificationSection>

                  <VerificationSection title="Notary Details">
                    <Row>
                      <Col md={6}>
                        <DetailRow label="Declared Name">
                          {notaryDetails?.DeclaredName}
                        </DetailRow>
                      </Col>
                      <Col md={6}>
                        <DetailRow label="Declared Commission Number">
                          {notaryDetails?.DeclaredCommissionNumber}
                        </DetailRow>
                      </Col>
                      <Col md={6}>
                        <DetailRow label="Declared State">
                          {notaryDetails?.DeclaredCommissionState}
                        </DetailRow>
                      </Col>
                      <Col md={6}>
                        <DetailRow label="Declared County">
                          {notaryDetails?.DeclaredCommissionCounty}
                        </DetailRow>
                      </Col>
                    </Row>
                    <DetailRow label="Declaration">
                      {notaryDetails?.DeclarationMessage}
                    </DetailRow>
                  </VerificationSection>

                  <VerificationSection title="Notarization Details">
                    <Row>
                      <Col md={6}>
                        <DetailRow label="Document">
                          {notarizationDetails?.DocumentType}
                        </DetailRow>
                      </Col>
                      <Col md={6}>
                        <DetailRow label="Notarial Act">
                          {notarizationDetails?.NotarialAct}
                        </DetailRow>
                      </Col>
                      <Col md={6}>
                        <DetailRow label="Signers">
                          <NameList items={notarizationDetails?.Signers} />
                        </DetailRow>
                      </Col>
                      <Col md={6}>
                        <DetailRow label="Document Witnesses">
                          <NameList
                            items={notarizationDetails?.DocumentWitnesses}
                          />
                        </DetailRow>
                      </Col>
                    </Row>
                  </VerificationSection>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Section>
  );
};

export default VerifyDocument;
